import { CoordinateChange } from "../../../../components/FlyerCoordinateEditor/types";
import {
  FlyerItemSource,
  GfFlyerItemListDto,
  GfMultiImage,
  SmartflyReviewStatus,
  ExtractionStatusResponseDto,
} from "@swagger/typescript-fetch-goflyer";
import { toastError, toastSuccess } from "../../../../utils/pageUtil/toast";
import {
  loadImageWithCORS,
  cropImage,
  getFlyerImageForPage,
  uploadImageToS3,
} from "../../../../utils/image";
import { adminApiService } from "../../../../utils/api";
import { COORDINATE_BATCH_SAVE_CONSTANTS } from "./coordinateBatchSaveConstants";

/**
 * Parameters for batch saving coordinate changes.
 *
 * @property changes - Array of coordinate changes to process
 * @property flyerItemListData - Flyer item list containing flyer images and metadata
 * @property authHeaders - Authorization headers for API requests
 * @property flyerItemListId - ID of the flyer item list
 */
interface CoordinateBatchSaveParams {
  changes: CoordinateChange[];
  flyerItemListData: GfFlyerItemListDto | null;
  authHeaders: { headers: { Authorization: string } };
  flyerItemListId?: string;
}

/**
 * Process AI extraction coordinates using the extraction API.
 *
 * Transforms coordinates to API format and polls for job completion.
 * - Converts coordinates to BBox format required by API
 * - Starts extraction job and receives jobId
 * - Polls job status every 5 seconds until completion
 * - Provides progress feedback via toast notifications
 *
 * @param aiExtractionChanges - Array of coordinate changes marked for AI extraction
 * @param flyerItemListData - Flyer item list containing flyer images
 * @param flyerItemListId - ID of the flyer item list
 * @param authHeaders - Authorization headers for API requests
 * @returns Promise that resolves when AI extraction is complete
 */
async function processAIExtractionCoordinates(
  aiExtractionChanges: CoordinateChange[],
  flyerItemListData: GfFlyerItemListDto | null,
  flyerItemListId: string,
  authHeaders: { headers: { Authorization: string } },
): Promise<void> {
  if (!flyerItemListData?.flyer?.id || !flyerItemListData?.flyer?.gfImages) {
    throw new Error("Flyer data is missing for AI extraction");
  }

  const sortedImages = [...flyerItemListData.flyer.gfImages].sort(
    (a, b) => (a.flyerIndex || 0) - (b.flyerIndex || 0),
  );

  const coordinateDtos = aiExtractionChanges.map((change) => {
    const coord = change.modifiedCoordinate;
    const flyerPageIndex = coord.flyerPage || 1;

    // Find image by matching flyerIndex
    const image = sortedImages.find((img) => img.flyerIndex === flyerPageIndex);

    if (!image?.id) {
      throw new Error(`No image found with flyerIndex ${flyerPageIndex}`);
    }

    const flyerIndex = image.flyerIndex;

    if (flyerIndex === undefined || flyerIndex === null) {
      throw new Error(
        `Image ${image.id} with flyerIndex ${flyerPageIndex} does not have a valid flyerIndex property`,
      );
    }

    return {
      bbox: {
        x1: Math.round(coord.x),
        y1: Math.round(coord.y),
        x2: Math.round(coord.x + coord.component.width),
        y2: Math.round(coord.y + coord.component.height),
      },
      imageId: image.id,
      flyerIndex: flyerIndex,
    };
  });

  const extractionRequest = {
    flyerId: flyerItemListData.flyer.id,
    flyerItemListId: flyerItemListId,
    coordinates: coordinateDtos,
  };

  const extractionResponse =
    await adminApiService.gfFlyerAdminControllerExtractFromCoordinates(
      extractionRequest,
      flyerItemListData.flyer.id,
      authHeaders,
    );

  const jobId = extractionResponse.jobId;

  toastSuccess(
    `AI extraction started: ${extractionResponse.totalCoordinates} coordinate${extractionResponse.totalCoordinates > 1 ? "s" : ""} queued for processing`,
  );

  await pollExtractionJobStatus(jobId, flyerItemListData.flyer.id, authHeaders);
}

/**
 * Poll the extraction job status until completion or failure.
 *
 * Uses exponential backoff polling to efficiently handle both fast and slow jobs:
 * - Starts at 2-second intervals to catch fast jobs quickly
 * - Increases intervals by 1.5x up to 10-second maximum
 * - Shows progress toasts every 30 seconds
 * - Times out after 10 minutes total elapsed time
 * - Continues until job reaches a terminal state (completed, failed, or cancelled)
 *
 * @param jobId - The extraction job ID to poll
 * @param flyerId - ID of the flyer
 * @param authHeaders - Authorization headers for API requests
 * @returns Promise that resolves when job is complete or fails
 */
async function pollExtractionJobStatus(
  jobId: string,
  flyerId: string,
  authHeaders: { headers: { Authorization: string } },
): Promise<void> {
  // Exponential backoff: start at 2s, cap at 10s → catches fast jobs quickly, avoids spamming slow ones
  const INITIAL_INTERVAL = 2000;
  const MAX_INTERVAL = 10000;
  const BACKOFF_FACTOR = 1.5;
  const MAX_TOTAL_TIME_MS = 10 * 60 * 1000; // 10 minutes
  const PROGRESS_TOAST_INTERVAL = 30000; // 30 seconds

  let elapsed = 0;
  let currentInterval = INITIAL_INTERVAL;
  let lastProgressToast = 0;

  while (elapsed < MAX_TOTAL_TIME_MS) {
    await new Promise((resolve) => setTimeout(resolve, currentInterval));
    elapsed += currentInterval;

    try {
      const statusResponse =
        await adminApiService.gfFlyerAdminControllerGetExtractionStatus(
          flyerId,
          jobId,
          authHeaders,
        );

      console.log(
        `AI extraction job ${jobId} status: ${statusResponse.status} (${statusResponse.completedCount}/${statusResponse.totalCount} processed, ${statusResponse.progress}% complete) [${Math.round(elapsed / 1000)}s elapsed]`,
      );

      if (
        statusResponse.status ===
        ExtractionStatusResponseDto.StatusEnum.Completed
      ) {
        toastSuccess(
          `AI extraction completed successfully! ${statusResponse.itemsCreated || statusResponse.completedCount} items extracted.`,
        );
        return;
      } else if (
        statusResponse.status === ExtractionStatusResponseDto.StatusEnum.Failed
      ) {
        const errorMessages = statusResponse.errors
          ?.map((err) => err.errorMessage)
          .join(", ");
        toastError(`AI extraction failed: ${errorMessages || "Unknown error"}`);
        throw new Error(`AI extraction job failed: ${errorMessages}`);
      } else if (
        statusResponse.status === ExtractionStatusResponseDto.StatusEnum.Unknown
      ) {
        toastError("AI extraction job status is unknown");
        throw new Error("AI extraction job status is unknown");
      } else if (
        statusResponse.status ===
          ExtractionStatusResponseDto.StatusEnum.Active ||
        statusResponse.status === ExtractionStatusResponseDto.StatusEnum.Waiting
      ) {
        // Job is still in progress — show progress toast every ~30 seconds
        if (elapsed - lastProgressToast >= PROGRESS_TOAST_INTERVAL) {
          lastProgressToast = elapsed;
          toastSuccess(
            `AI extraction in progress: ${statusResponse.completedCount}/${statusResponse.totalCount} processed (${statusResponse.progress}%)...`,
          );
        }
        // Increase interval with exponential backoff, capped at MAX_INTERVAL
        currentInterval = Math.min(
          currentInterval * BACKOFF_FACTOR,
          MAX_INTERVAL,
        );
      }
    } catch (error) {
      console.error("Error polling extraction job status:", error);
      toastError("Failed to check AI extraction status");
      throw error;
    }
  }

  toastError(
    "AI extraction job timed out. Please check the job status manually.",
  );
  throw new Error("AI extraction job polling timed out");
}

/**
 * Yields control back to the main thread, allowing the browser to process pending
 * events and UI updates. Call between batches to prevent UI freezing on large saves.
 */
const yieldToMainThread = (): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, 0));

/**
 * Process promises with a concurrency limit to prevent overwhelming system resources.
 *
 * Uses a counter-based pool: starts up to `limit` tasks simultaneously, and as each
 * task completes it immediately starts the next queued one. This guarantees at most
 * `limit` tasks are in-flight at any time, preventing memory exhaustion and API rate
 * limiting when processing large batches.
 *
 * @param tasks - Array of async functions to execute
 * @param limit - Maximum number of concurrent operations
 * @returns Array of settled results in input order
 */
async function processBatchWithConcurrencyLimit<T>(
  tasks: (() => Promise<T>)[],
  limit: number,
): Promise<PromiseSettledResult<T>[]> {
  const results: PromiseSettledResult<T>[] = new Array(tasks.length);

  if (tasks.length === 0) return results;

  return new Promise((resolve) => {
    let active = 0;
    let nextIndex = 0;

    const runNext = () => {
      while (active < limit && nextIndex < tasks.length) {
        const idx = nextIndex++;
        active++;

        tasks[idx]()
          .then((value) => {
            results[idx] = { status: "fulfilled", value };
          })
          .catch((reason) => {
            results[idx] = { status: "rejected", reason };
          })
          .finally(() => {
            active--;
            if (nextIndex < tasks.length) {
              runNext();
            } else if (active === 0) {
              resolve(results);
            }
          });
      }
    };

    runNext();
  });
}

/**
 * Process regular coordinate changes (non-AI extraction).
 *
 * Handles the traditional coordinate save workflow:
 * - Groups changes by page for efficient processing
 * - Loads flyer images and crops product images
 * - Uploads to S3 and creates GfImage records
 * - Updates flyer items with coordinates and images
 * - Sets appropriate itemSource flags
 * - Updates review status
 *
 * @param changes - Array of regular coordinate changes
 * @param flyerItemListData - Flyer item list containing flyer images and metadata
 * @param authHeaders - Authorization headers for API requests
 * @returns Promise that resolves when all changes are processed
 */
async function processRegularCoordinates(
  changes: CoordinateChange[],
  flyerItemListData: GfFlyerItemListDto | null,
  authHeaders: { headers: { Authorization: string } },
): Promise<void> {
  const results = {
    successful: 0,
    failed: 0,
    failedItems: [] as string[],
  };

  const changesByPage = new Map<number, CoordinateChange[]>();
  changes.forEach((change) => {
    const page =
      change.modifiedCoordinate?.flyerPage ||
      COORDINATE_BATCH_SAVE_CONSTANTS.PAGE_FALLBACK;
    if (!changesByPage.has(page)) {
      changesByPage.set(page, []);
    }
    changesByPage.get(page)!.push(change);
  });

  const sortedPages = Array.from(changesByPage.keys()).sort((a, b) => a - b);

  const totalChanges = changes.length;
  const totalPages = sortedPages.length;
  let completedCount = 0;
  let lastProgressToast = 0;
  const showProgressToasts =
    totalChanges > COORDINATE_BATCH_SAVE_CONSTANTS.PROGRESS_UPDATE_INTERVAL;

  console.log(
    `Processing regular coordinates: ${totalChanges} change(s) across ${totalPages} page(s)`,
  );

  if (flyerItemListData?.id) {
    try {
      await adminApiService.updateOneBaseGfFlyerItemListAdminControllerGfFlyerItemList(
        {
          reviewStatus: SmartflyReviewStatus.Corrected,
        },
        flyerItemListData.id,
        authHeaders,
      );
      toastSuccess('Flyer review status updated to "In Review"');
    } catch (error) {
      console.error("Failed to update flyerItemList reviewStatus:", error);
      toastError("Failed to update review status");
    }
  }

  for (const page of sortedPages) {
    const pageChanges = changesByPage.get(page)!;
    const pageIndex = sortedPages.indexOf(page) + 1;

    console.log(
      `Processing page ${page} (${pageIndex}/${totalPages}): ${pageChanges.length} item(s)...`,
    );

    const flyerImage = getFlyerImageForPage(
      flyerItemListData?.flyer?.gfImages || [],
      page,
    );

    let blobUrl: string | null = null;

    if (flyerImage?.src) {
      try {
        blobUrl = await loadImageWithCORS(flyerImage.src);
      } catch (error) {
        console.error(`Failed to load image for page ${page}:`, error);
        toastError(`Failed to load flyer image for page ${page}`);
        pageChanges.forEach(() => {
          results.failed++;
        });
        continue;
      }
    }

    const pageTasks = pageChanges.map((change) => async () => {
      try {
        let itemSource: FlyerItemSource | undefined;
        if (!change.originalCoordinate) {
          itemSource = FlyerItemSource.HumanAdded;
        } else if (change.flyerItem.itemSource === FlyerItemSource.AiDetected) {
          itemSource = FlyerItemSource.HumanAdjusted;
        } else if (change.flyerItem.itemSource) {
          itemSource = change.flyerItem.itemSource;
        } else {
          itemSource = FlyerItemSource.HumanAdjusted;
        }

        let updatedFlyerItem = {
          ...change.flyerItem,
          coordinate: change.modifiedCoordinate,
          itemSource,
        };

        if (
          blobUrl &&
          change.modifiedCoordinate?.x !== undefined &&
          change.modifiedCoordinate?.y !== undefined &&
          change.modifiedCoordinate?.component?.width &&
          change.modifiedCoordinate?.component?.height
        ) {
          try {
            const flyerItemListId =
              flyerItemListData?.id ||
              COORDINATE_BATCH_SAVE_CONSTANTS.UNKNOWN_FLYER_ITEM_LIST_ID;
            const imageFileName =
              `${COORDINATE_BATCH_SAVE_CONSTANTS.CROPPED_IMAGE_FILENAME_PREFIX}-` +
              `${flyerItemListId}-${Date.now()}-${Math.random().toString(36).substring(7)}.png`;
            const croppedFile = await cropImage(
              blobUrl,
              change.modifiedCoordinate,
              imageFileName,
            );

            const merchantName =
              flyerItemListData?.stores?.[0]?.merchant?.name ||
              COORDINATE_BATCH_SAVE_CONSTANTS.UNKNOWN_MERCHANT;
            const sanitizedMerchantName = merchantName
              .replace(/[^a-zA-Z0-9-_]/g, "-")
              .toLowerCase();
            const startDate = flyerItemListData?.validStartDate
              ? new Date(flyerItemListData.validStartDate)
                  .toISOString()
                  .split("T")[0]
              : "";
            const endDate = flyerItemListData?.validEndDate
              ? new Date(flyerItemListData.validEndDate)
                  .toISOString()
                  .split("T")[0]
              : "";
            const dateRange =
              startDate && endDate
                ? `${startDate}_${endDate}`
                : COORDINATE_BATCH_SAVE_CONSTANTS.DATE_RANGE_FALLBACK;
            const s3Path =
              `${COORDINATE_BATCH_SAVE_CONSTANTS.S3_PATH_PREFIX}/` +
              `${sanitizedMerchantName}/${dateRange}/flyer-items`;

            const uploadResult = await uploadImageToS3(
              croppedFile,
              false,
              s3Path,
            );

            if (uploadResult) {
              const resizeSmall =
                COORDINATE_BATCH_SAVE_CONSTANTS.IMAGE_PROCESSING.RESIZE_SMALL;
              const splitLargeImage =
                COORDINATE_BATCH_SAVE_CONSTANTS.IMAGE_PROCESSING
                  .SPLIT_LARGE_IMAGE;

              const imageProcessingResult =
                await adminApiService.gfImageAdminControllerHandleImageProcessing(
                  uploadResult.src,
                  undefined,
                  resizeSmall,
                  splitLargeImage,
                );

              const gfImageData = {
                ...uploadResult,
                srcSmall: imageProcessingResult?.srcSmall,
                gfSplitImages: imageProcessingResult?.gfSplitImages || [],
              };

              const gfImagesResponse =
                await adminApiService.createManyBaseGfImageAdminControllerGfImage(
                  {
                    bulk: [gfImageData],
                  },
                );

              if (gfImagesResponse && gfImagesResponse.length > 0) {
                const croppedGfImage = gfImagesResponse[0];

                const newGfMultiImage: Partial<GfMultiImage> = {
                  gfImage: croppedGfImage,
                };

                updatedFlyerItem = {
                  ...updatedFlyerItem,
                  gfMultiImages: [newGfMultiImage as GfMultiImage],
                };

                console.log(
                  `Item ${change.flyerItemId}: Cropped image uploaded and processed`,
                );
              }
            }
          } catch (cropError) {
            console.error(
              `Error cropping/uploading image for item ${change.flyerItemId}:`,
              cropError,
            );
          }
        }

        await adminApiService.updateOneBaseGfFlyerItemAdminControllerGfFlyerItem(
          updatedFlyerItem,
          change.flyerItemId,
          authHeaders,
        );

        results.successful++;
        completedCount++;
        if (
          showProgressToasts &&
          completedCount - lastProgressToast >=
            COORDINATE_BATCH_SAVE_CONSTANTS.PROGRESS_UPDATE_INTERVAL
        ) {
          lastProgressToast = completedCount;
          toastSuccess(
            `Saving: ${completedCount}/${totalChanges} items saved...`,
          );
        }
        return { success: true, itemId: change.flyerItemId };
      } catch (error) {
        console.error(`Failed to update item ${change.flyerItemId}:`, error);
        results.failed++;
        completedCount++;
        results.failedItems.push(`Item ${change.flyerItemId} on page ${page}`);
        return { success: false, itemId: change.flyerItemId, error };
      }
    });

    const pageResults = await processBatchWithConcurrencyLimit(
      pageTasks,
      COORDINATE_BATCH_SAVE_CONSTANTS.MAX_CONCURRENT_ITEMS,
    );

    const pageSuccesses = pageResults.filter(
      (result) => result.status === "fulfilled" && result.value.success,
    ).length;
    const pageFailures = pageResults.length - pageSuccesses;

    if (pageFailures > 0) {
      toastError(
        `Page ${page}: ${pageSuccesses} succeeded, ${pageFailures} failed`,
      );
    } else if (!showProgressToasts) {
      toastSuccess(`Page ${page}: ${pageSuccesses} item(s) saved successfully`);
    }

    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
    }

    await yieldToMainThread();
  }

  if (results.failed === 0) {
    toastSuccess(
      `Batch save complete: ${results.successful} item(s) updated successfully`,
    );
  } else {
    toastError(
      `Batch save completed with errors: ${results.successful} succeeded, ${results.failed} failed`,
    );
    if (results.failedItems.length > 0) {
      console.error("Failed items:", results.failedItems);
    }
  }
}

/**
 * Main entry point for batch saving coordinate changes.
 *
 * Separates AI extraction coordinates from regular coordinates and processes them appropriately:
 * - AI extraction coordinates are sent to the extraction API with job polling
 * - Regular coordinates follow the traditional save workflow
 * - Both processes run independently
 *
 * @param params - Batch save parameters containing changes, flyer data, auth headers, and flyer item list ID
 * @returns Promise that resolves when all changes are processed
 */
export async function handleCoordinateBatchSave({
  changes,
  flyerItemListData,
  authHeaders,
  flyerItemListId,
}: CoordinateBatchSaveParams): Promise<void> {
  // Separate AI extraction coordinates from regular coordinate changes
  const aiExtractionChanges = changes.filter((change) => change.isAIExtraction);
  const regularChanges = changes.filter((change) => !change.isAIExtraction);

  console.log(
    `Starting batch save: ${changes.length} total change(s) - ` +
      `${aiExtractionChanges.length} AI extraction, ${regularChanges.length} regular`,
  );

  if (aiExtractionChanges.length === 0 && regularChanges.length === 0) {
    toastSuccess("No changes to save");
    return;
  }

  const [aiResult, regularResult] = await Promise.allSettled([
    aiExtractionChanges.length > 0 && flyerItemListId
      ? processAIExtractionCoordinates(
          aiExtractionChanges,
          flyerItemListData,
          flyerItemListId,
          authHeaders,
        )
      : Promise.resolve(),
    regularChanges.length > 0
      ? processRegularCoordinates(
          regularChanges,
          flyerItemListData,
          authHeaders,
        )
      : Promise.resolve(),
  ]);

  const errors: string[] = [];

  if (aiResult.status === "rejected") {
    console.error("AI extraction failed:", aiResult.reason);
    toastError("AI extraction failed. Please try again.");
    errors.push(
      `AI extraction: ${aiResult.reason?.message || "Unknown error"}`,
    );
  }

  if (regularResult.status === "rejected") {
    console.error("Regular coordinate save failed:", regularResult.reason);
    toastError("Coordinate save failed. Please try again.");
    errors.push(
      `Coordinate save: ${regularResult.reason?.message || "Unknown error"}`,
    );
  }

  if (errors.length > 0) {
    throw new Error(`Batch save partial failure:\n${errors.join("\n")}`);
  }
}
